import { useState, useCallback, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, ChevronRight, FileText, Menu, MoreHorizontal, X } from "lucide-react";
import type { DocCategoryGroup } from "../types";
import { ShareFolderDialog } from "./ShareFolderDialog";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocsNavSidebarProps {
  /** Grouped document categories to render in the sidebar */
  categories: DocCategoryGroup[];
  /** Whether the current user is an internal Entra user. Controls 3-dot share menu visibility. */
  isInternal?: boolean;
  /** When set (scope="folder"), restrict sidebar to only this folder_id. */
  scopedFolderId?: string;
}

// ---------------------------------------------------------------------------
// Category section with collapsible toggle
// ---------------------------------------------------------------------------

function CategorySection({
  group,
  isInternal,
}: {
  group: DocCategoryGroup;
  isInternal: boolean;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-2">
      <div className="flex items-center">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex flex-1 items-center gap-2 rounded-ev px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ev-text-secondary transition-colors hover:bg-ev-surface-subtle"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          {group.label}
          <span className="ml-auto text-[10px] font-normal text-ev-text-muted">
            {group.items.length}
          </span>
        </button>

        {isInternal && (
          <ShareFolderDialog folderId={group.category} folderLabel={group.label}>
            {(open) => (
              <button
                onClick={open}
                aria-label={`Share ${group.label} folder`}
                title={`Share ${group.label} folder`}
                className="ml-0.5 shrink-0 rounded-ev p-1.5 text-ev-text-muted transition-colors hover:bg-ev-surface-subtle hover:text-ev-info-text"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            )}
          </ShareFolderDialog>
        )}
      </div>

      {expanded && (
        <div className="mt-0.5 space-y-0.5 pl-2">
          {group.items.map((item) => (
            <NavLink
              key={item.slug}
              to={`/docs/page/${item.slug}`}
              className={({ isActive }) =>
                [
                  "flex items-center gap-2 rounded-ev px-3 py-1.5 text-sm transition-colors",
                  isActive
                    ? "bg-ev-info-bg font-medium text-ev-info-text"
                    : "text-ev-text-primary hover:bg-ev-surface-subtle",
                ]
                  .filter(Boolean)
                  .join(" ")
              }
              title={item.title}
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-ev-text-muted" />
              <span className="truncate">{item.title}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop sidebar (resizable)
// ---------------------------------------------------------------------------

const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 320;

function DesktopSidebar({ categories, isInternal = false, scopedFolderId }: DocsNavSidebarProps) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_WIDTH);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width]);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    }

    function onMouseUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  return (
    <aside
      className="relative hidden shrink-0 border-r border-ev-border bg-ev-surface lg:block"
      style={{ width }}
    >
      <div className="flex h-full flex-col overflow-y-auto p-4">
        <div className="mb-4 text-sm font-semibold text-ev-text-primary">
          Explainers
        </div>
        <nav className="flex-1" aria-label="Explainers navigation">
          {(scopedFolderId
            ? categories.filter((g) => g.category === scopedFolderId)
            : categories
          ).map((group) => (
            <CategorySection key={group.category} group={group} isInternal={isInternal} />
          ))}
        </nav>
      </div>

      {/* Drag handle */}
      <div
        onMouseDown={onMouseDown}
        className="absolute inset-y-0 right-0 w-1 cursor-col-resize transition-colors hover:bg-ev-info-text/30"
        title="Drag to resize"
      />
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Mobile sidebar overlay
// ---------------------------------------------------------------------------

function MobileSidebarOverlay({
  open,
  onClose,
  categories,
  isInternal = false,
  scopedFolderId,
}: {
  open: boolean;
  onClose: () => void;
  categories: DocCategoryGroup[];
  isInternal?: boolean;
  scopedFolderId?: string;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar panel — wider on mobile for readability */}
      <aside className="fixed inset-y-0 left-0 w-80 bg-ev-surface shadow-lg">
        <div className="flex h-full flex-col overflow-y-auto">
          {/* Header with close button */}
          <div className="flex items-center justify-between border-b border-ev-border px-4 py-3">
            <span className="text-sm font-semibold text-ev-text-primary">
              Explainers
            </span>
            <button
              onClick={onClose}
              className="rounded-ev p-1 text-ev-text-secondary transition-colors hover:bg-ev-surface-subtle hover:text-ev-text-primary"
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav sections */}
          <nav className="flex-1 p-4" aria-label="Explainers navigation">
            {(scopedFolderId
              ? categories.filter((g) => g.category === scopedFolderId)
              : categories
            ).map((group) => (
              <CategorySection key={group.category} group={group} isInternal={isInternal} />
            ))}
          </nav>
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported DocsNavSidebar
// ---------------------------------------------------------------------------

export function DocsNavSidebar({ categories, isInternal = false, scopedFolderId }: DocsNavSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile hamburger trigger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed bottom-4 left-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-ev-info-text text-white shadow-lg lg:hidden print:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop sidebar */}
      <DesktopSidebar categories={categories} isInternal={isInternal} scopedFolderId={scopedFolderId} />

      {/* Mobile overlay */}
      <MobileSidebarOverlay
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        categories={categories}
        isInternal={isInternal}
        scopedFolderId={scopedFolderId}
      />
    </>
  );
}
